import { DiscordMarkdown } from "@/components/markdown/DiscordMarkdown";
import LonghouseModal from "@/components/modals/LonghouseModal";
import { PushNotificationButton } from "@/components/notifications/PushNotificationButton";
import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import {
    BellIcon,
    CheckIcon,
    EnvelopeIcon,
    EnvelopeOpenIcon,
    MegaphoneIcon,
    TrashIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const NOTIFICATIONS_FULL_QUERY = gql`
    query NotificationsFull(
        $unreadOnly: Boolean
        $notificationType: String
        $limit: Int
        $offset: Int
    ) {
        notifications(
            unreadOnly: $unreadOnly
            notificationType: $notificationType
            limit: $limit
            offset: $offset
        ) {
            id
            notificationType
            title
            message
            isRead
            createdAt
            readAt
            actionUrl
            imageUrl
            relatedObjectId
            relatedObjectType
            sender {
                id
                twitchUsername
            }
            isBroadcast
            isGroupMessage
        }
        notificationCount {
            total
            unread
        }
    }
`;

const MARK_NOTIFICATION_AS_READ = gql`
    mutation MarkNotificationAsRead($notificationId: UUID!) {
        markNotificationAsRead(notificationId: $notificationId) {
            success
        }
    }
`;

const MARK_NOTIFICATION_AS_UNREAD = gql`
    mutation MarkNotificationAsUnread($notificationId: UUID!) {
        markNotificationAsUnread(notificationId: $notificationId) {
            success
        }
    }
`;

const MARK_ALL_NOTIFICATIONS_AS_READ = gql`
    mutation MarkAllNotificationsAsRead {
        markAllNotificationsAsRead {
            success
            count
        }
    }
`;

const DELETE_NOTIFICATION = gql`
    mutation DeleteNotification($notificationId: UUID!) {
        deleteNotification(notificationId: $notificationId) {
            success
        }
    }
`;

const NOTIFICATION_SUBSCRIPTION = gql`
    subscription OnNotificationReceived {
        notificationReceived {
            id
            notificationType
            title
            message
            isRead
            createdAt
            readAt
            actionUrl
            imageUrl
            relatedObjectId
            relatedObjectType
            sender {
                id
                twitchUsername
            }
            isBroadcast
            isGroupMessage
        }
    }
`;

interface Notification {
    id: string;
    notificationType: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    readAt?: string;
    actionUrl?: string;
    imageUrl?: string;
    sender?: {
        id: string;
        twitchUsername: string;
    };
    isBroadcast: boolean;
    isGroupMessage: boolean;
}

export default function LonghouseInboxPage() {
    const [searchParams] = useSearchParams();
    const notificationId = searchParams.get("notification");
    const notificationRefs = useRef<{ [key: string]: HTMLElement | null }>({});
    // If navigating to a specific notification, show all notifications so it's visible
    const [filter, setFilter] = useState<"all" | "unread">(
        notificationId ? "all" : "unread",
    );
    const [selectedNotification, setSelectedNotification] =
        useState<Notification | null>(null);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const limit = 50;

    // Modal state
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deleteNotificationId, setDeleteNotificationId] = useState<
        string | null
    >(null);

    const { data, loading, refetch, client } = useQuery(
        NOTIFICATIONS_FULL_QUERY,
        {
            variables: {
                unreadOnly: filter === "unread",
                limit,
                offset,
            },
            onCompleted: (data) => {
                // Check if we have more notifications to load
                if (data?.notifications) {
                    setHasMore(data.notifications.length === limit);
                }
            },
        },
    );

    // Subscribe to new notifications
    useSubscription(NOTIFICATION_SUBSCRIPTION, {
        onData: ({ data: subscriptionData }) => {
            console.debug(
                "[LonghouseInboxPage] Subscription data received:",
                subscriptionData,
            );
            if (subscriptionData?.data?.notificationReceived) {
                const newNotification =
                    subscriptionData.data.notificationReceived;
                console.debug(
                    "[LonghouseInboxPage] New notification:",
                    newNotification,
                );

                // Add __typename for Apollo cache and ensure all required fields exist
                const notificationWithTypename = {
                    __typename: "NotificationType",
                    imageUrl: null,
                    relatedObjectId: null,
                    relatedObjectType: null,
                    ...newNotification,
                };

                // Directly update the query cache
                try {
                    const existingData = client.cache.readQuery<{
                        notifications: Notification[];
                        notificationCount: { total: number; unread: number };
                    }>({
                        query: NOTIFICATIONS_FULL_QUERY,
                        variables: {
                            unreadOnly: filter === "unread",
                            limit,
                            offset,
                        },
                    });

                    if (existingData) {
                        // Check if notification already exists
                        const exists = existingData.notifications.some(
                            (n: Notification) => n.id === newNotification.id,
                        );

                        if (!exists) {
                            client.cache.writeQuery({
                                query: NOTIFICATIONS_FULL_QUERY,
                                variables: {
                                    unreadOnly: filter === "unread",
                                    limit,
                                    offset,
                                },
                                data: {
                                    notifications: [
                                        notificationWithTypename,
                                        ...existingData.notifications,
                                    ],
                                    notificationCount: {
                                        __typename: "NotificationCountType",
                                        total:
                                            existingData.notificationCount
                                                .total + 1,
                                        unread:
                                            existingData.notificationCount
                                                .unread + 1,
                                    },
                                },
                            });
                            console.debug(
                                "[LonghouseInboxPage] Added notification to cache",
                            );
                        } else {
                            console.debug(
                                "[LonghouseInboxPage] Notification already in cache",
                            );
                        }
                    }
                } catch (error) {
                    console.error(
                        "[LonghouseInboxPage] Cache update error:",
                        error,
                    );
                    // Fallback to refetch if cache update fails
                    refetch();
                }
            }
        },
        onError: (error) => {
            console.error("[LonghouseInboxPage] Subscription error:", error);
        },
    });

    const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
        onCompleted: () => {
            refetch();
            // Update the selected notification's read status immediately
            if (selectedNotification) {
                setSelectedNotification({
                    ...selectedNotification,
                    isRead: true,
                    readAt: new Date().toISOString(),
                });
            }
        },
    });

    const [markAsUnread] = useMutation(MARK_NOTIFICATION_AS_UNREAD, {
        onCompleted: () => {
            refetch();
            // Update the selected notification's read status immediately
            if (selectedNotification) {
                setSelectedNotification({
                    ...selectedNotification,
                    isRead: false,
                    readAt: undefined,
                });
            }
        },
    });

    const [markAllAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
        onCompleted: () => {
            refetch();
            // If viewing an unread notification, update it
            if (selectedNotification && !selectedNotification.isRead) {
                setSelectedNotification({
                    ...selectedNotification,
                    isRead: true,
                    readAt: new Date().toISOString(),
                });
            }
        },
    });

    useEffect(() => {
        if (
            notificationId &&
            notificationRefs.current[notificationId] &&
            data
        ) {
            setTimeout(() => {
                notificationRefs.current[notificationId]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                // Auto-select the notification if it's in the URL
                const notification = data.notifications?.find(
                    (n: Notification) => n.id === notificationId,
                );
                if (notification) {
                    setSelectedNotification(notification);
                    if (!notification.isRead) {
                        markAsRead({ variables: { notificationId } });
                    }
                }
            }, 100);
        }
    }, [notificationId, data, markAsRead]);

    const [deleteNotification] = useMutation(DELETE_NOTIFICATION, {
        onCompleted: () => {
            refetch();
            setSelectedNotification(null);
        },
    });

    const notifications = data?.notifications || [];
    const notificationCount = data?.notificationCount;

    const handleSelectNotification = (notification: Notification) => {
        setSelectedNotification(notification);
        if (!notification.isRead) {
            markAsRead({ variables: { notificationId: notification.id } });
        }
    };

    const handleDelete = (notificationId: string) => {
        setDeleteNotificationId(notificationId);
        setConfirmModalOpen(true);
    };

    const confirmDelete = () => {
        if (deleteNotificationId) {
            deleteNotification({
                variables: { notificationId: deleteNotificationId },
            });
            setDeleteNotificationId(null);
        }
        setConfirmModalOpen(false);
    };

    const handleFilterChange = (newFilter: "all" | "unread") => {
        setFilter(newFilter);
        setOffset(0);
        setHasMore(true);
        // Clear selection if current notification doesn't match new filter
        if (selectedNotification) {
            if (newFilter === "unread" && selectedNotification.isRead) {
                setSelectedNotification(null);
            }
        }
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            setOffset(offset + limit);
        }
    };

    const loadPrevious = () => {
        if (offset > 0) {
            setOffset(Math.max(0, offset - limit));
        }
    };

    const getNotificationIcon = (type: string) => {
        const iconClass = "h-5 w-5";
        switch (type) {
            case "stream_team_invitation":
                return (
                    <EnvelopeIcon className={`${iconClass} text-purple-500`} />
                );
            case "stream_team_acceptance":
                return <CheckIcon className={`${iconClass} text-green-500`} />;
            case "stream_team_request":
                return (
                    <UserGroupIcon className={`${iconClass} text-blue-500`} />
                );
            case "stream_team_application_status":
                return <BellIcon className={`${iconClass} text-yellow-500`} />;
            case "admin_message":
                return (
                    <MegaphoneIcon className={`${iconClass} text-orange-500`} />
                );
            case "shield_tag_earned":
                return <CheckIcon className={`${iconClass} text-amber-500`} />;
            case "system_announcement":
                return (
                    <MegaphoneIcon className={`${iconClass} text-red-500`} />
                );
            default:
                return <BellIcon className={`${iconClass} text-slate-500`} />;
        }
    };

    return (
        <div className="h-full px-4 py-4">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                            Inbox
                        </h1>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                            {notificationCount?.total || 0} total notifications,{" "}
                            {notificationCount?.unread || 0} unread
                        </p>
                    </div>
                    <PushNotificationButton variant="longhouse" />
                </div>
            </div>

            <div className="mb-4 flex gap-4">
                <button
                    onClick={() => setFilter("all")}
                    className={`rounded-lg px-4 py-2 font-medium ${
                        filter === "all"
                            ? "bg-brand text-white"
                            : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                >
                    All
                </button>
                <button
                    onClick={() => setFilter("unread")}
                    className={`rounded-lg px-4 py-2 font-medium ${
                        filter === "unread"
                            ? "bg-brand text-white"
                            : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                >
                    Unread ({notificationCount?.unread || 0})
                </button>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Notifications List */}
                <div className="overflow-hidden rounded-lg bg-white shadow lg:col-span-1 dark:bg-slate-800">
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                        {loading && (
                            <div className="p-8 text-center text-slate-600 dark:text-slate-400">
                                Loading...
                            </div>
                        )}

                        {!loading && notifications.length === 0 && (
                            <div className="p-8 text-center text-slate-600 dark:text-slate-400">
                                <BellIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                                <p>No notifications found</p>
                            </div>
                        )}

                        {!loading &&
                            notifications.map((notification: Notification) => (
                                <button
                                    key={notification.id}
                                    ref={(el) => {
                                        notificationRefs.current[
                                            notification.id
                                        ] = el;
                                    }}
                                    onClick={() =>
                                        handleSelectNotification(notification)
                                    }
                                    className={`w-full p-4 text-left transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-700 ${
                                        selectedNotification?.id ===
                                        notification.id
                                            ? "bg-slate-50 dark:bg-slate-700"
                                            : ""
                                    } ${
                                        !notification.isRead
                                            ? "bg-blue-50 dark:bg-slate-700/50"
                                            : ""
                                    } ${
                                        notificationId === notification.id
                                            ? "ring-brand ring-2 ring-offset-2 dark:ring-offset-slate-800"
                                            : ""
                                    }`}
                                >
                                    <div className="flex gap-3">
                                        <div className="mt-1 shrink-0">
                                            {getNotificationIcon(
                                                notification.notificationType,
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <p
                                                    className={`text-sm ${
                                                        notification.isRead
                                                            ? "text-slate-600 dark:text-slate-400"
                                                            : "font-semibold text-slate-900 dark:text-white"
                                                    }`}
                                                >
                                                    {notification.title}
                                                </p>
                                                {!notification.isRead && (
                                                    <span className="bg-brand h-2 w-2 shrink-0 rounded-full"></span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                                                {formatDistanceToNow(
                                                    new Date(
                                                        notification.createdAt,
                                                    ),
                                                    { addSuffix: true },
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                    </div>
                    {/* Pagination Controls */}
                    {(offset > 0 || hasMore) && (
                        <div className="border-t border-slate-200 p-4 dark:border-slate-700">
                            <div className="flex justify-between">
                                <button
                                    onClick={loadPrevious}
                                    disabled={offset === 0}
                                    className="rounded-lg px-4 py-2 text-sm font-medium transition-colors enabled:border enabled:border-slate-300 enabled:bg-white enabled:text-slate-700 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 enabled:dark:border-slate-600 enabled:dark:bg-slate-800 enabled:dark:text-slate-300 enabled:dark:hover:bg-slate-700"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={loadMore}
                                    disabled={!hasMore || loading}
                                    className="rounded-lg px-4 py-2 text-sm font-medium transition-colors enabled:border enabled:border-slate-300 enabled:bg-white enabled:text-slate-700 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 enabled:dark:border-slate-600 enabled:dark:bg-slate-800 enabled:dark:text-slate-300 enabled:dark:hover:bg-slate-700"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>{" "}
                {/* Notification Detail */}
                <div className="rounded-lg bg-white p-6 shadow lg:col-span-2 dark:bg-slate-800">
                    {!selectedNotification && (
                        <div className="py-12 text-center text-slate-600 dark:text-slate-400">
                            <BellIcon className="mx-auto mb-4 h-16 w-16 text-slate-400" />
                            <p>Select a notification to view details</p>
                        </div>
                    )}

                    {selectedNotification && (
                        <div>
                            <div className="mb-6 flex items-start justify-between">
                                <div className="flex gap-3">
                                    <div className="text-4xl">
                                        {getNotificationIcon(
                                            selectedNotification.notificationType,
                                        )}
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                            {selectedNotification.title}
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                                            {formatDistanceToNow(
                                                new Date(
                                                    selectedNotification.createdAt,
                                                ),
                                                { addSuffix: true },
                                            )}
                                            {selectedNotification.sender && (
                                                <span>
                                                    {" "}
                                                    • From{" "}
                                                    {
                                                        selectedNotification
                                                            .sender
                                                            .twitchUsername
                                                    }
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            if (selectedNotification.isRead) {
                                                markAsUnread({
                                                    variables: {
                                                        notificationId:
                                                            selectedNotification.id,
                                                    },
                                                });
                                            } else {
                                                markAsRead({
                                                    variables: {
                                                        notificationId:
                                                            selectedNotification.id,
                                                    },
                                                });
                                            }
                                        }}
                                        className="rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                                        title={
                                            selectedNotification.isRead
                                                ? "Mark as unread"
                                                : "Mark as read"
                                        }
                                    >
                                        {selectedNotification.isRead ? (
                                            <EnvelopeOpenIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
                                        ) : (
                                            <EnvelopeIcon className="h-6 w-6 text-blue-500" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() =>
                                            handleDelete(
                                                selectedNotification.id,
                                            )
                                        }
                                        className="rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                                        title="Delete"
                                    >
                                        <TrashIcon className="h-6 w-6 text-red-500" />
                                    </button>
                                </div>
                            </div>

                            {selectedNotification.imageUrl && (
                                <div className="mb-6">
                                    <img
                                        src={selectedNotification.imageUrl}
                                        alt="Notification"
                                        className="max-w-full rounded-lg border border-slate-200 dark:border-slate-700"
                                        loading="lazy"
                                    />
                                </div>
                            )}

                            <DiscordMarkdown className="prose prose-invert max-w-none text-slate-200">
                                {selectedNotification.message}
                            </DiscordMarkdown>

                            {selectedNotification.actionUrl && (
                                <div className="mt-6 border-t border-slate-200 pt-6 dark:border-slate-700">
                                    <Link
                                        to={selectedNotification.actionUrl}
                                        className="bg-brand hover:bg-brand/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-white"
                                    >
                                        View Details
                                    </Link>
                                </div>
                            )}

                            {(selectedNotification.isBroadcast ||
                                selectedNotification.isGroupMessage) && (
                                <div className="mt-4 flex gap-2">
                                    {selectedNotification.isBroadcast && (
                                        <span className="inline-flex items-center rounded bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                            Broadcast
                                        </span>
                                    )}
                                    {selectedNotification.isGroupMessage && (
                                        <span className="inline-flex items-center rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                            Group Message
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <LonghouseModal
                isOpen={confirmModalOpen}
                onClose={() => {
                    setConfirmModalOpen(false);
                    setDeleteNotificationId(null);
                }}
                onConfirm={confirmDelete}
                title="Delete Notification"
                message="Are you sure you want to delete this notification?"
                type="confirm"
            />
        </div>
    );
}
